<template>
  <div class="help-center-category-tree__component">
    <template
      v-for="entry of tree"
      :key="entry.id"
    >
      <HelpCenterCategoryItem
        v-if="entry.isItem"
        :category="entry.category"
        :is-item="entry.isItem"
        :is-primary="entry.isPrimary"
        :selected-category="selectedCategory"
        @navigate="onNavigate"
      />
      <details
        v-else
        :open="entry.isOpen"
        class="help-center-category-tree__details"
      >
        <HelpCenterCategoryItem
          :category="entry.category"
          :is-item="entry.isItem"
          :is-primary="entry.isPrimary"
          :selected-category="selectedCategory"
          with-chevrons
          chevron-up-class="help-center-category-tree__chevron-up"
          chevron-down-class="help-center-category-tree__chevron-down"
          class="help-center-category-tree__item"
        />
        <HelpCenterCategoryTree
          :type="type"
          :level="nextLevel"
          :branch="entry.branch"
          :selected-category="selectedCategory"
          :class="nextClass"
          @navigate="onNavigate"
        />
      </details>
    </template>
  </div>
</template>

<script setup>
import { ref, toRef, watch, computed } from 'vue'
import HelpCenterCategoryItem from 'help-center/components/HelpCenterCategoryItem'
import HELP_CENTER_TYPES from 'help-center/constants/types'

const emit = defineEmits(['navigate'])
const props = defineProps({
  type: {
    type: String,
    required: true
  },
  level: {
    type: Number,
    default: 0
  },
  branch: Array,
  categories: Array,
  selectedCategory: Object
})

const rootTree = ref([])

const tree = computed(() => {
  return props.branch ?? rootTree.value
})

const nextClass = computed(() => {
  if (props.level >= 1) {
    return 'help-center-category-tree__nested-in-node'
  } else {
    return 'help-center-category-tree__nested-in-root'
  }
})

const nextLevel = computed(() => {
  return props.level + 1
})

watch(toRef(props, 'categories'), (value) => {
  if (value) {
    rootTree.value = treeSeed(
      props.type,
      props.categories,
      props.selectedCategory,
      props.level
    )
  }
}, { immediate: true })

function onNavigate() {
  emit('navigate')
}

function treeSeed(type, categories, selectedCategory, level) {
  const isPrimary = level === 0
  return categories.map((category, index) => {
    const id = category.id

    if (!category.categories) {
      const isSelected = category === selectedCategory
      return { id, category, isPrimary, isItem: true, isSelected }
    }

    const selfOpen = type === HELP_CENTER_TYPES.UPDATES || (!selectedCategory && index === 0)
    const branch = treeSeed(type, category.categories, selectedCategory, level + 1)
    const isOpen = selfOpen || branch.some((child) => child.isSelected || child.isOpen)
    return { id, category, isPrimary, isItem: false, isOpen, branch }
  })
}
</script>

<style lang="scss">
.help-center-category-tree {
  &__details[open] > &__item &__chevron-down {
    display: none;
  }

  &__details:not([open]) > &__item &__chevron-up {
    display: none;
  }

  &__component, &__details {
    > * + * {
      margin-top: 8px !important;
    }
  }

  &__nested-in-root {
    padding-left: 24px;
  }

  &__nested-in-node {
    padding-left: 12px;
  }
}
</style>
